import * as Yup from 'yup';

import { Roles, BackOfficeRoles } from 'types';
import { ltlInvoiceNumberSchema } from 'utils/ltl-invoice-number';
import {
  StockLtlFields,
  StockLtlEditRequest,
} from 'store/api/stock-orders/types';
import {
  sameEventSplit,
  isEventSplitType,
  eventSplitSchema,
  isDefaultOnlySplit,
  buildEventAllocations,
  getEventSplitInitialValues,
} from 'components/stock-transfer-event-split/config';

import {
  LtlEditFormValues,
  StockLtlTransferFacts,
  StockLtlStoredShipping,
} from './types';

// Owner rule L4a: Russ's invoice # arrives after delivery, so on a Done stock
// order / Completed stock transfer the Company Owner and Administrative staff
// may still set LTL Invoice # and LTL Price (everything else stays locked).
export const LTL_EDIT_ROLES: string[] = [
  Roles.COMPANY_OWNER,
  BackOfficeRoles.ADMINISTRATIVE,
];

export const canEditCompletedLtl = (role?: string | null) =>
  !!role && LTL_EDIT_ROLES.includes(role);

// StockOrders/StockTransfers.ltlPrice is DECIMAL(10, 2).
const LTL_PRICE_PATTERN = /^\d{1,8}(\.\d{1,2})?$/;

export const ltlEditValidationSchema = Yup.object().shape({
  ltlInvoiceNumber: ltlInvoiceNumberSchema,
  workOrder: Yup.string().nullable().max(255),
  // owner 2026-10-19: a transfer's tracking # (Main: text up to 255)
  trackingNumber: Yup.string().nullable().max(255),
  ltlPrice: Yup.string().nullable().trim().matches(LTL_PRICE_PATTERN, {
    excludeEmptyString: true,
    message: 'LTL Price must be an amount with at most 2 decimals',
  }),
});

export const getLtlEditInitialValues = (
  stored: Partial<
    Pick<StockLtlFields, 'ltlPrice' | 'workOrder' | 'ltlInvoiceNumber'>
  >
): LtlEditFormValues => ({
  ltlPrice: stored.ltlPrice ?? '',
  workOrder: stored.workOrder ?? '',
  ltlInvoiceNumber: stored.ltlInvoiceNumber ?? '',
});

/**
 * Owner 2026-10-16: a transfer's work order in the same dialog (only when
 * Main's route answers it): '' clears it (null), unchanged is not sent.
 */
export const withWorkOrderChange = <T extends object>(
  body: T,
  values: LtlEditFormValues,
  stored: { workOrder?: string | null }
): T & { workOrder?: string | null } => {
  const next = (values.workOrder ?? '').trim() || null;
  const before = (stored.workOrder ?? '').trim() || null;
  return next === before ? body : { ...body, workOrder: next };
};

/**
 * Owner 2026-10-19: a transfer's carrier and tracking # in the same dialog
 * (only when Main's route answers them), as last read.
 */
export const getShippingInitialValues = (
  stored: StockLtlStoredShipping
): Pick<LtlEditFormValues, 'carrierId' | 'trackingNumber'> => ({
  carrierId: stored.carrierId ?? null,
  trackingNumber: stored.trackingNumber ?? '',
});

/**
 * Only what changed: a cleared tracking # is sent as '' (Main stores null),
 * a cleared carrier as null; an unchanged one is not sent.
 */
export const withShippingChange = <T extends object>(
  body: T,
  values: LtlEditFormValues,
  stored: StockLtlStoredShipping
): T & { trackingNumber?: string; carrierId?: number | null } => {
  const next: { trackingNumber?: string; carrierId?: number | null } = {};
  const carrier = values.carrierId ?? null;
  if (carrier !== (stored.carrierId ?? null)) {
    next.carrierId = carrier;
  }
  const tracking = (values.trackingNumber ?? '').trim();
  if (tracking !== (stored.trackingNumber ?? '').trim()) {
    next.trackingNumber = tracking;
  }
  return { ...body, ...next };
};

const normalizeInvoice = (value?: string | null) =>
  (typeof value === 'string' ? value.trim() : '') || null;

const normalizePrice = (value?: string | number | null) => {
  const text =
    value === null || value === undefined ? '' : String(value).trim();
  return text || null;
};

const samePrice = (a: string | null, b: string | null) =>
  a === null || b === null ? a === b : Number(a) === Number(b);

/**
 * Body for PATCH .../:id/ltl: only the fields that really changed. A cleared
 * field is sent as null; an unchanged one is not sent at all.
 */
export const buildLtlEditBody = (
  values: LtlEditFormValues,
  stored: Partial<Pick<StockLtlFields, 'ltlPrice' | 'ltlInvoiceNumber'>>
): Omit<StockLtlEditRequest, 'id'> => {
  const body: Omit<StockLtlEditRequest, 'id'> = {};

  const invoice = normalizeInvoice(values.ltlInvoiceNumber);
  if (invoice !== normalizeInvoice(stored.ltlInvoiceNumber)) {
    body.ltlInvoiceNumber = invoice;
  }

  const price = normalizePrice(values.ltlPrice);
  if (!samePrice(price, normalizePrice(stored.ltlPrice))) {
    body.ltlPrice = price;
  }

  return body;
};

/**
 * T-L09 (owner rule L14) / owner 2026-10-16: on every transfer type but
 * Client -> Client the dialog also edits the optional event split; the price
 * and the split are saved together (the shares must add up to the price).
 */
export const hasEventSplit = (transfer?: StockLtlTransferFacts) =>
  isEventSplitType(transfer?.type);

export const ltlEditSplitValidationSchema = ltlEditValidationSchema.shape({
  events: eventSplitSchema('ltlPrice'),
});

export const getLtlEditSplitInitialValues = (
  stored: Partial<Pick<StockLtlFields, 'ltlPrice' | 'ltlInvoiceNumber'>>,
  transfer?: StockLtlTransferFacts
): LtlEditFormValues => ({
  ...getLtlEditInitialValues(stored),
  events: getEventSplitInitialValues(transfer?.eventAllocations),
});

/**
 * buildLtlEditBody plus `eventAllocations` when the split changed. Rows that
 * say only the default (its event at 100%) are no split.
 */
export const buildLtlEditSplitBody = (
  values: LtlEditFormValues,
  stored: Partial<Pick<StockLtlFields, 'ltlPrice' | 'ltlInvoiceNumber'>>,
  transfer?: StockLtlTransferFacts
) => {
  const body = buildLtlEditBody(values, stored);
  const saved = getEventSplitInitialValues(transfer?.eventAllocations);
  if (sameEventSplit(values.events, saved)) {
    return body;
  }
  // without a saved split, the default event alone stays no split
  const rows =
    !saved.length &&
    isDefaultOnlySplit(values.events, transfer?.defaultEventId, values.ltlPrice)
      ? []
      : values.events || [];
  return sameEventSplit(rows, saved)
    ? body
    : { ...body, eventAllocations: buildEventAllocations(rows) };
};

// RTK Query error shape from store/query.ts (axiosBaseQuery).
export const isLtlRouteUnavailable = (error: unknown) =>
  (error as { status?: number } | undefined)?.status === 404;
